import { CloudOff, Ban, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OfflineOrder } from '@/lib/offline/offlineStore'
import { formatTimeHuman } from '@/lib/utils'

/**
 * The held order the cart is currently adding to (F-FE-N31).
 *
 * This is the offline twin of SentItemsSection, and deliberately NOT that
 * component: SentItemsSection takes an `Order`, and a held order has no server
 * row, no id, no totals and no payments. Faking one so the rail would accept it
 * would put an invented total on screen and let it leak into the pricing and
 * payment paths — the exact class of mistake the "never show assumed numbers"
 * rule exists to stop.
 *
 * So it shows only what this device actually knows: the number already printed
 * on the kitchen ticket, and the lines already fired. No money. The bill is
 * computed server-side when the order replays.
 */
export function OfflineHeldTicket({
  order,
  nameFor,
  onClear,
  onVoidLine,
}: {
  order: OfflineOrder
  /** Resolves a product id to its menu name; falls back to the id if unknown. */
  nameFor: (productId: string) => string
  onClear: () => void
  /**
   * Cancel a line the kitchen is already making (F-CTRL-N56). Omit to render
   * read-only. The caller prints the void chit — removing the line here without
   * telling the kitchen would mean the food is made and nobody is charged.
   */
  onVoidLine?: (lineIndex: number) => void
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <CloudOff className="h-4 w-4 shrink-0" />
          <span className="truncate">
            Adding to <span className="font-mono">#{order.ticketRef || order.orderNumber}</span>
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 px-2 text-amber-900 hover:bg-amber-200/50 dark:hover:bg-amber-900/40 dark:text-amber-200"
          onClick={onClear}
        >
          <X className="mr-1 h-3.5 w-3.5" />
          Done
        </Button>
      </div>

      <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
        Offline order, saved on this device — already sent to the kitchen at{' '}
        {formatTimeHuman(order.capturedAt)}
        . New items print their own ticket and join this order when it syncs.
      </p>

      <ul className="mt-2 space-y-0.5 text-sm">
        {order.lines.map((l, i) => (
          // Index-keyed on purpose: two rounds of the same product are two
          // distinct rows that must both render. A void re-indexes the rows
          // after it, which is why the handler is given the index at click time
          // rather than the row holding one.
          <li key={`${l.productId}-${i}`} className="flex items-baseline gap-2">
            <span className="w-6 shrink-0 text-right font-medium tabular-nums text-muted-foreground">
              {l.quantity}×
            </span>
            <span className="min-w-0 flex-1">
              <span className="truncate">{nameFor(l.productId)}</span>
              {l.special_instructions ? (
                <span className="block text-xs italic text-muted-foreground">
                  {l.special_instructions}
                </span>
              ) : null}
            </span>
            {onVoidLine && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Void ${nameFor(l.productId)}`}
                title="Cancel this item and print a void chit"
                className="h-6 shrink-0 px-1.5 text-xs text-destructive hover:bg-destructive/10"
                onClick={() => onVoidLine(i)}
              >
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {(order.voids?.length ?? 0) > 0 && (
        <p className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/80">
          {order.voids?.length} item{order.voids?.length === 1 ? '' : 's'} cancelled — a void
          chit was printed for the kitchen.
        </p>
      )}
    </div>
  )
}
